/**
 * Restore resume structure when LLM returns run-on text.
 * Ensures: one line per bullet, section headers on their own line, contact details split.
 * Used for display, TXT/PDF/DOCX export, and in backend after optimization.
 */

/** Section header phrases for regex (case-insensitive) */
const SECTION_PATTERN =
  '(Professional Summary|Summary|Objective|Work Experience|Experience|Employment|Education|Skills|Technical Skills|Certifications|Awards|Honors|Publications|Key Achievements|Core Competencies|Expertise|Languages\\s*&\\s*Frameworks|Cloud\\s*&\\s*DevOps|ML\\s*&\\s*Data Science|Large Language Models|Product Management\\s*&\\s*Design Tools|References|Additional Information|Contact Information|Contact)';

/** Section headers that may appear mid-line in PDF-extracted text. Longest first so we break correctly. */
const MIDLINE_SECTION_PHRASES = [
  'Product Management & Design Tools',
  'Languages & Frameworks',
  'Cloud & DevOps Technologies',
  'Cloud & DevOps',
  'ML & Data Science',
  'AI/ML & Data Science',
  'Technical Proficiencies',
  'Technical Expertise',
  'Summary of Qualifications',
  'Executive Summary',
  'Career Summary',
  'Professional Summary',
  'Employment History',
  'Work Experience',
  'Work History',
  'Professional Experience',
  'Career History',
  'Academic Background',
  'Academic Qualifications',
  'Key Achievements',
  'Core Competencies',
  'Contact Information',
  'Personal Information',
  'Personal Details',
  'Technical Skills',
  'Domain Expertise',
  'Market Strategy',
  'Professional Development',
  'Continuing Education',
  'Volunteer Experience',
  'Volunteer Work',
  'Community Service',
  'Professional Affiliations',
  'Summary',
  'Objective',
  'Career Objective',
  'Experience',
  'Employment',
  'Education',
  'Skills',
  'Certifications',
  'Certificates',
  'Awards',
  'Honors',
  'Awards & Honors',
  'Projects',
  'Achievements',
  'Publications',
  'Patents',
  'Research',
  'Training',
  'Leadership',
  'Activities',
  'References',
  'Expertise',
  'Strengths',
  'Hobbies',
  'Contact',
  'Languages',
  'Technologies',
  'Frameworks',
];

/**
 * Insert newline before section headers when they appear mid-line so the parser can split sections.
 */
function breakBeforeSectionHeaders(text: string): string {
  let out = text;
  for (const phrase of MIDLINE_SECTION_PHRASES) {
    const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(
      `([^\\n])(\\s*)(${escaped})(?=\\s*[:\\-]|\\s|$)`,
      'gi'
    );
    out = out.replace(re, (_: string, before: string, space: string, header: string) => {
      return `${before}${space}\n\n${header}`;
    });
  }
  return out;
}

/** Bullet chars (Unicode + ASCII) so run-on lines from any resume tool split onto separate lines. */
const BULLET_CHARS = '[•*▪∙●◦○·\u2022\u25AA\u2219\u25CF\u25E6\u25CB\u00B7]';

/**
 * Put each bullet on its own line. Handles • * ▪ ∙ ● ◦ ○ · and common variants.
 */
function splitBulletsOntoLines(text: string): string {
  let out = text;
  const bulletClass = new RegExp(`\\s+(${BULLET_CHARS})\\s+`, 'g');
  out = out.replace(bulletClass, '\n$1 ');
  // After period/newline, " - " followed by capital = new bullet (avoid splitting dates like "01/2020 - 12/2022")
  out = out.replace(/([.!?\n])\s*-\s+([A-Z])/g, '$1\n- $2');
  return out;
}

/**
 * Ensure section headers are preceded by a blank line when they appear mid-line.
 */
function ensureSectionBreaks(text: string): string {
  let out = text;
  const re = new RegExp('([.!?])\\s*(' + SECTION_PATTERN + ')\\s*[:]?', 'gi');
  out = out.replace(re, (_: string, end: string, header: string) => `${end}\n\n${header.trim()}`);
  // Break before "Languages &", "Cloud &", "ML &" when they appear after other content
  out = out.replace(/\s+(Languages\s*&\s*Frameworks)/gi, '\n\n$1');
  out = out.replace(/\s+(Cloud\s*&\s*DevOps)/gi, '\n\n$1');
  out = out.replace(/\s+(ML\s*&\s*Data Science)/gi, '\n\n$1');
  out = out.replace(/\s+(Product\s*Management\s*&\s*Design\s*Tools)/gi, '\n\n$1');
  return out;
}

/**
 * Split long contact-style lines (contain @ and |) into one item per line.
 */
function splitContactLines(text: string): string {
  const lines = text.split(/\n/);
  const result: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length > 80 && trimmed.includes('@') && trimmed.includes('|')) {
      const parts = trimmed.split(/\s*\|\s*/).map((p) => p.trim()).filter(Boolean);
      result.push(...parts);
    } else {
      result.push(line);
    }
  }
  return result.join('\n');
}

/**
 * Normalize line breaks: trim each line, collapse 4+ newlines to 2.
 */
function normalizeLineBreaks(text: string): string {
  const lines = text.split(/\r?\n/).map((l) => l.trimEnd());
  const out: string[] = [];
  let prevBlank = false;
  for (const line of lines) {
    const isBlank = line.trim().length === 0;
    if (isBlank && prevBlank) continue;
    out.push(line);
    prevBlank = isBlank;
  }
  return out.join('\n').replace(/\n{3,}/g, '\n\n').trim();
}

/**
 * Restore resume structure so that:
 * - Each bullet (• * -) is on its own line
 * - Section headers have a blank line before them
 * - Long contact lines (email | phone | location) are split
 * - Excess blank lines collapsed
 */
export function restoreResumeStructure(text: string): string {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  out = breakBeforeSectionHeaders(out);
  out = splitBulletsOntoLines(out);
  out = ensureSectionBreaks(out);
  out = splitContactLines(out);
  out = normalizeLineBreaks(out);
  return out;
}
